import { mergeAttributes, Node, textblockTypeInputRule } from '@tiptap/core';

export type Level = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface HeadingOptions {
  levels: Level[];
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    heading: {
      setDefaultHeading: (attributes: { level: Level }) => ReturnType;
      toggleDefaultHeading: (attributes: { level: Level }) => ReturnType;
    };
  }
}

export const CustomHeading = Node.create<HeadingOptions>({
  name: 'heading',

  addOptions() {
    return {
      levels: [1, 2, 3, 4, 5, 6, 7, 8],
      HTMLAttributes: {},
    };
  },

  content: 'inline*',
  group: 'block',
  defining: true,
  addAttributes() {
    return {
      level: {
        default: 1,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return this.options.levels.map((level: Level) => ({
      tag: `h${level}`,
      attrs: { level },
    }));
  },

  renderHTML({ node, HTMLAttributes }) {
    const hasLevel = this.options.levels.includes(node.attrs.level);
    const level = hasLevel ? node.attrs.level : this.options.levels[0];

    return [`h${level}`, mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setDefaultHeading: ({ level }) => {
        return ({ commands }) => {
          if (!this.options.levels.includes(level)) return false;

          const attrs = {
            level,
          };

          return commands.setNode(this.name, attrs);
        };
      },

      toggleDefaultHeading: ({ level }) => {
        return ({ commands }) => {
          if (!this.options.levels.includes(level)) return false;

          const attrs = {
            level,
          };

          return commands.toggleNode(this.name, 'paragraph', attrs);
        };
      },
    };
  },

  addKeyboardShortcuts() {
    return this.options.levels.reduce((shortcuts, level) => {
      shortcuts[`Mod-Alt-${level}`] = () => this.editor.commands.toggleDefaultHeading({ level });
      return shortcuts;
    }, {} as Record<string, () => boolean>);
  },

  addInputRules() {
    return this.options.levels.map((level) => {
      return textblockTypeInputRule({
        find: new RegExp(`^(#{${level}})\\s$`),
        type: this.type,
        getAttributes: () => ({ level }),
      });
    });
  },
});
